/**
 * `DeckScene` projections — the demo's `buildDeck(m)` (demo line 2613): an
 * eight-slide equipment summary of one mine over 18 months.
 *
 * 1. cover · 2. at a glance · 3. cost per operating hour by class ·
 * 4. monthly maintenance spend · 5. fleet condition · 6. the open predictive
 * alert (or the breakdown history) · 7. energy and emissions ·
 * 8. recommendations.
 *
 * Everything is derived from the lazy domains and the eager core except the
 * recommendations, which are the demo's own words per site: Broken Ridge's
 * come from the exported deck (`reports.json` → `deck_br`), the other five
 * are the demo's literals with their live figures substituted (Kalbarri's
 * late-PM count and store lines; the cost-per-hour pair).
 *
 * The OEM's name on the cover follows the selected brand; pass the identity.
 *
 * Pure: no React, no clock, no fetching.
 *
 * @see src/versions/v1/modules/reports/DeckScene.tsx
 * @see docs/logic/reports/deck.md
 */

import { BY_ID, COST_PER_MACHINE, HEALTH, IDS, MINES, MINE_ORDER } from '../../data/core';
import type { CostByMonthMine } from '../../schema/cost';
import type { MachineClass, MineCode } from '../../schema/machine';
import type { DeckPack, DeckReport } from '../../schema/report';
import type { MachineEnergy } from '../../schema/telemetry';
import type { OemDocIdentity } from '../../shared/docs/doc-identity';
import {
  CLS_ORDER,
  fleetCostPerOpHourByMine,
  groupNum,
  mineWeek,
  WK,
  type InventoryCtx,
  type MineWeek,
  type ShiftCtx,
  type WorkOrderCtx,
} from '../../shared/oem-engine';
import {
  REPORT_DOWNLOAD,
  REPORT_FILE_DATE,
  REPORT_FORMAT,
  reportSlug,
} from '../../shared/report-catalog';
import { COST_CATEGORIES } from '../../shared/viz/tokens';

/** What the deck reads. */
export interface DeckCtx extends WorkOrderCtx, ShiftCtx, InventoryCtx {
  readonly costByMonth: readonly CostByMonthMine[];
  readonly energy: readonly MachineEnergy[];
  /** `reports.deck_br` — Broken Ridge's exported recommendations. */
  readonly pack: DeckPack;
}

/** Demo 2618. The class labels on slide 3. */
export const DECK_CLASS_LABEL: Readonly<Record<MachineClass, string>> = {
  jumbo: 'Dev. jumbo (MJ-2D)',
  longhole: 'Production rig (MP-60/60A)',
  lhd: 'Loader (ML series)',
  truck: 'Haulage truck (MT-50/63)',
  sdrill: 'Surface drill (MS-45/120)',
};

/** Demo 2613. The 18-month window of the cost slides. */
const FROM_MONTH = '2025-03';
const TO_MONTH = '2026-08';

/** Demo 2617. Slide 8, per site. */
export function deckRecommendations(
  m: MineCode,
  mw: MineWeek,
  pack: DeckPack,
  favg: Readonly<Record<MineCode, number>>,
  oem: OemDocIdentity,
): string[] {
  if (m === 'BR') return [...pack.recommendations];
  if (m === 'MR') {
    return [
      "Approve WO-26-11917 and SO-90435 today: L-33's charging connector fails within three to five more charge cycles, and the only connector in Tasmania is at Burnie — Hamish carries it on Thursday rather than three days on Bass Strait freight.",
      "Take the charging interfaces off the wash-down route on all six ML-21E (TB-2026-034); the insulation-resistance fall after Saturday's wash is the whole story of this alert.",
      "Charger contention is the fleet's real availability loss: hold the staggered rotation (MR-BEV-001) and cost the fourth bay on the 500 level — on the hydro grid the battery loaders run at about 8 kg CO₂-e an hour against 78 for a diesel ML-21, so the fleet is doing what it was bought to do.",
      "J-17's junction box has taken water twice in the North lenses — fit the seal kit (SO-90436) and add a breather; the third earth-leakage trip would cost a shift.",
      'Load the Western Tiers contract PM plan beside the OEM plan and flag every interval that is longer — the Gawler Deep hose-kit lesson applies to a contractor-mined site.',
    ];
  }
  if (m === 'OH') {
    return [
      "Approve WO-26-11913: S-04's hammer exchange at Thursday's pattern change — the penetration-rate signature gives 40–70 drilling hours, and the fishing job on S-05 showed what a failed string costs (A$24.3k against A$4.6k planned).",
      'Adopt SP-MS-PIPE-007 as the site inspection standard and trend breakout torque from the RMS feed on every string; the S-05 signature was in the data for twelve days (TB-2026-033).',
      "Dust: S-06's collector cartridges are blinding every three shifts in the oxide — check the pulse-valve solenoid before buying cartridges; dust exposure is the site's biggest hygiene item.",
      'Stock: hold two exchange hammers and six pipes at the site store rather than one and two — Darwin is three days by road and the site cannot air-freight a hammer.',
      'Move Ochre Hill to L2 so the agent can draft requisitions directly against the Darwin DC minimums.',
    ];
  }
  if (m === 'GD') {
    return [
      'Close the contract/OEM PM-plan gap first: four ML-21 hose kits were past the OEM 4,000 h interval because the contract plan carried 6,000 h; the L-18 fire (A$98.9k) was the cost of that gap. Approve the campaign (WO-26-11916 first).',
      "Approve WO-26-11914 and SO-90431 for S-09's rotary head motor and keep it on the pre-split row until fitted; the torque oscillation gets worse with hole depth.",
      'Turn the hydraulic top-up trend into a Watch condition on every loader (5 L per shift) — it is now live on the northern fleet.',
      "L-20's turbocharger (boost −25%) is the next failure in the central workings — approve WO-26-11915 before it derates on a loaded climb.",
      `Keep the Maximo ↔ SAP PM cross-reference: every ${oem.short} WO now carries the GDEEP work order number, so the customer's system stays the system of record.`,
    ];
  }
  if (m === 'KG') {
    return [
      `Bring PM compliance up first: ${mw.late} PMs ran late since 1 Aug and reactive work averages ~9 h down here against ~5 h at the other sites — that is the whole cost-per-hour gap (A$${favg.KG}/h vs A$${favg.BR}/h at Broken Ridge).`,
      `Adopt TB-2026-031 250 h oil sampling on every ML-21: the ${IDS.L14} coupling failure (A$15,250 all-in, warranty declined) was contamination plus ramming, and ${IDS.L03} failed the same way in February.`,
      'Operator practice: bucket-ramming events on Night B are counted by the FMS every shift — put the count on the shift handover and review it weekly with the supervisor.',
      `Site store minimums: ${mw.low.length} lines below minimum; hold one hydraulic pump coupling and one filter cart on site rather than relying on overnight freight from Perth.`,
      'Move to the live-data tier: Kalbarri only sees a shift-end export, so a two-week temperature drift is invisible until the machine stops.',
    ];
  }
  return [
    `Approve the drafted brake inspection on ${IDS.T22} (WO-26-11912) and keep the empty-haul restriction until pads and discs are measured; four exceedances >240 °C in a shift is the SP-MT63-BRK-002 trigger.`,
    'Decline brake temperature trend for every MT-63 on the 1:7 — the live feed makes this a weekly KPI rather than a post-failure finding.',
    `${IDS.L31}: separate machine derates (intake air, air filter at PM-250) from Wi-Fi handover dropouts (CS-2026-2188) so the tele-remote loader is not blamed for the network.`,
    `Seismic exclusions stay coded to ground conditions (MI-SEIS-002): ${mw.name}'s per-hour cost is already the second lowest; don't let exclusion hours distort the contractor's numbers.`,
    'Battery-electric L-12: confirm fast-charge coolant flow and module temperature spread at shift change; hold one MER-BAT-MOD on site.',
  ];
}

/** Demo 2613–2631. `buildDeck(m)`, as data. */
export function deckReport(m: MineCode, ctx: DeckCtx, oem: OemDocIdentity): DeckReport {
  const mw = mineWeek(m, ctx);
  const M = MINES[m];

  // Slides 2 and 4 — the ledger over the 18-month window.
  const cm = ctx.costByMonth.filter(
    (c) => c.mine === m && c.month >= FROM_MONTH && c.month <= TO_MONTH,
  );
  const months = [...new Set(cm.map((c) => c.month))].sort();
  const monthly = months.map((month) => ({
    month,
    amount: cm.filter((c) => c.month === month).reduce((a, c) => a + c.amount_aud, 0),
  }));
  const cost18 = cm.reduce((a, c) => a + c.amount_aud, 0);
  const byCategory = COST_CATEGORIES.map((category) => ({
    category,
    amount: cm.filter((x) => x.category === category).reduce((a, x) => a + x.amount_aud, 0),
  }));

  // Slide 3 — mean cost per operating hour of each class at the site.
  const costPerClass = CLS_ORDER.flatMap((cls) => {
    const xs = COST_PER_MACHINE.filter(
      (x) => BY_ID[x.machine_id]?.mine === m && BY_ID[x.machine_id]?.cls === cls,
    );
    if (!xs.length) return [];
    const value = +(xs.reduce((a, x) => a + x.cost_per_op_hour_aud, 0) / xs.length).toFixed(0);
    return [{ label: DECK_CLASS_LABEL[cls], value }];
  });
  const favg = Object.fromEntries(
    MINE_ORDER.map((mm) => [mm, fleetCostPerOpHourByMine(mm)]),
  ) as Record<MineCode, number>;

  // Slide 7 — mean kg CO₂-e per operating hour by drive, in first-seen order.
  const byDrive = new Map<string, number[]>();
  for (const e of ctx.energy) {
    if (e.mine !== m) continue;
    byDrive.set(e.drive, [...(byDrive.get(e.drive) ?? []), e.co2_kg_per_hour]);
  }
  const energy = [...byDrive].map(([drive, v]) => ({
    drive,
    co2PerHour: +(v.reduce((a, b) => a + b, 0) / v.length).toFixed(1),
    machines: v.length,
  }));

  // Slide 6 — the first open alert at the site, and the three costliest breakdowns.
  const alert = mw.alerts[0] ?? null;
  const breakdowns = ctx.workOrders
    .filter((w) => w.mine === m && w.wo_type === 'Breakdown')
    .map((w) => ({ w, allin: w.total_direct_cost_aud + w.downtime_cost_aud }))
    .sort((a, b) => b.allin - a.allin)
    .slice(0, 3)
    .map(({ w, allin }) => ({
      wo_id: w.wo_id,
      machine_id: w.machine_id,
      component: w.component,
      date: w.created.slice(0, 10),
      allin,
    }));

  const fileName = `${reportSlug(mw.name)}_Equipment-Summary_${REPORT_FILE_DATE}.html`;
  return {
    kind: 'deck',
    mine: m,
    title: `${mw.name} — equipment summary`,
    sub: `8 slides · 18-month review, ${WK.short}${m !== 'BR' ? ` · built live for ${mw.name}` : ''}`,
    docId: `${reportSlug(mw.name)} equipment summary · 8 slides`,
    format: REPORT_FORMAT.deck,
    download: REPORT_DOWNLOAD.deck,
    fileName,
    cover: {
      title: `${mw.name} — equipment summary`,
      sub: `${mw.n} ${oem.short} machines · ${M.state} · ${groupNum(M.depth_m)} m · ${M.operator_model}`,
      meta: `Mar 2025 – Sep 2026 (18 months) · ${WK.short} · ${oem.legal} · ${oem.division} · Internal — customer shareable`,
    },
    glance: {
      availability: mw.avail,
      cost18: Math.round(cost18),
      cph: favg[m],
      cphOthers: MINE_ORDER.filter((x) => x !== m)
        .map((x) => `${MINES[x].name.split(' ')[0]} A$${favg[x]}`)
        .join(' · '),
      co2_t: mw.co2_t,
      diesel: mw.diesel,
      kwh: mw.kwh,
    },
    costPerClass,
    monthly,
    byCategory,
    condition: mw.machines.map((r) => ({
      machine_id: r.machine_id,
      model: r.model,
      engine_hours: r.engine_hours,
      avail: r.avail,
      band: r.band,
      worst: r.worst,
      score: r.score,
      cph: r.cph,
    })),
    alert: alert
      ? {
          id: alert.id,
          machine: alert.machine,
          comp: alert.comp,
          title: alert.title,
          window: alert.window,
          confidence: alert.conf,
          why: alert.why,
        }
      : null,
    breakdowns,
    energy,
    week: { diesel: mw.diesel, kwh: mw.kwh, co2_t: mw.co2_t, n: mw.n },
    recommendations: deckRecommendations(m, mw, ctx.pack, favg, oem),
    sources: [
      { label: 'Cost ledger by month', dataset: 'ledger', count: `${cm.length} rows` },
      { label: 'Cost per operating hour', dataset: 'cpm', count: `${mw.n} machines` },
      { label: 'Shift records', dataset: 'shift', count: `${mw.days} machine-days` },
      {
        label: 'Condition register',
        dataset: 'health',
        count: `${HEALTH.filter((h) => BY_ID[h.machine_id]?.mine === m).length} rows`,
      },
      { label: 'Energy by machine', dataset: 'energy', count: '12 days' },
    ],
  };
}

/** Demo 2627. Slide 4's x-axis label — `2025-03` → `25/03`. */
export const monthTick = (month: string): string => month.slice(2).replace('-', '/');
